/**
 * AudiencePage.tsx — admin "Audience" section (owner ask 2026-09-29).
 *
 * Shows the fleet's follower / subscriber counts over time from the daily snapshots
 * (GET /audience/series) with the option to filter creators OUT. Default = the total of ALL
 * live creators; excluding a creator subtracts her from every point (carry-forward totals
 * are recomputed server-side, so the numbers always agree with the table).
 *
 * Layout: title + capture-now · ONE filter row (range → metric → creators) · three stat
 * tiles · the line chart card (with a table twin) · the per-creator table whose "Include"
 * column is the same filter state as the creators control.
 *
 * Security: admin-only route (RolesGuard on /audience/*); no financials on this page.
 */

import * as React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiRequestError,
  audienceApi,
  type AudienceAccountDto,
  type AudienceSeriesPointDto,
} from '../../lib/api-client.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@crm/ui';
import { AudienceChart, METRIC_LABELS, type MetricKey } from './AudienceChart.js';
import { addDays, formatDayLong, formatInt, formatSigned, summarize, utcDay } from './chart-math.js';
import { formatBerlin } from '../../lib/time.js';

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

type RangeKey = '7d' | '30d' | '90d' | 'all';

const RANGES: Array<{ key: RangeKey; label: string; days: number }> = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: 'all', label: 'All', days: 400 }, // MAX_SPAN_DAYS on the API
];

const METRICS: MetricKey[] = ['followers', 'subscribers', 'fansTotal'];

function creatorLabel(a: AudienceAccountDto): string {
  return a.displayName?.trim() || (a.username ? `@${a.username}` : 'Creator');
}

function statusBadge(a: AudienceAccountDto): React.ReactNode {
  if (a.lastError) {
    const text = a.lastError.startsWith('skipped:')
      ? a.lastError.slice('skipped:'.length).replace(/_/g, ' ')
      : a.lastError.startsWith('capture_failed:')
        ? 'capture failed'
        : a.lastError.replace(/_/g, ' ');
    return (
      <Badge variant="warning" title={a.lastError}>
        {text}
      </Badge>
    );
  }
  if (a.connectionStatus === 'online') return <Badge variant="success">online</Badge>;
  return <Badge variant="default">{a.connectionStatus.replace(/_/g, ' ')}</Badge>;
}

// ---------------------------------------------------------------------------
// Chip (filter toggle) — plain button styled like the nav pills
// ---------------------------------------------------------------------------

function Chip({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={[
        'px-2.5 py-1 text-xs rounded font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active
          ? 'bg-accent/20 text-accent border-accent/40'
          : 'bg-transparent text-text-muted border-border hover:text-text hover:bg-bg-hover',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AudiencePage() {
  const qc = useQueryClient();
  const { addToast } = useToast();

  const [range, setRange] = React.useState<RangeKey>('30d');
  const [metric, setMetric] = React.useState<MetricKey>('followers');
  const [excluded, setExcluded] = React.useState<Set<string>>(() => new Set());
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showTable, setShowTable] = React.useState(false);

  const today = utcDay();
  const rangeDef = RANGES.find((r) => r.key === range) ?? RANGES[1]!;
  const from = addDays(today, -(rangeDef.days - 1));
  const excludedList = React.useMemo(() => [...excluded].sort(), [excluded]);

  const accountsQ = useQuery({
    queryKey: ['audience', 'accounts'],
    queryFn: () => audienceApi.accounts(),
  });

  const seriesQ = useQuery({
    queryKey: ['audience', 'series', from, today, excludedList],
    queryFn: () => audienceApi.series({ from, to: today, exclude: excludedList }),
    placeholderData: keepPreviousData,
  });

  // --- capture now -----------------------------------------------------------
  const [polling, setPolling] = React.useState(false);
  const statusQ = useQuery({
    queryKey: ['audience', 'capture-status'],
    queryFn: () => audienceApi.captureStatus(),
    refetchInterval: polling ? 4_000 : false,
  });

  React.useEffect(() => {
    if (!polling) return;
    if (statusQ.data && statusQ.data.running === false) {
      setPolling(false);
      const s = statusQ.data.lastSummary;
      if (s) {
        addToast(
          `Capture finished: ${s.captured} captured, ${s.failed} failed, ${s.skipped} skipped`,
          s.failed > 0 ? 'warning' : 'success',
        );
      }
      void qc.invalidateQueries({ queryKey: ['audience'] });
    }
  }, [polling, statusQ.data, addToast, qc]);

  const capture = useMutation({
    mutationFn: () => audienceApi.capture(),
    onSuccess: () => {
      addToast('Capture started — this takes a couple of minutes across all creators.', 'default');
      setPolling(true);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        addToast('A capture is already running.', 'warning');
        setPolling(true);
      } else if (err instanceof ApiRequestError && err.status === 503) {
        addToast('Snapshots are disabled on this server (worker spine off).', 'danger');
      } else {
        addToast('Could not start a capture.', 'danger');
      }
    },
  });

  // --- derived ---------------------------------------------------------------
  const accounts = accountsQ.data ?? [];
  const points: AudienceSeriesPointDto[] = seriesQ.data?.points ?? [];
  const summary = summarize(points.map((p) => p[metric]));
  const last = points.length > 0 ? points[points.length - 1]! : null;
  const includedCount = accounts.filter((a) => !excluded.has(a.accountId)).length;

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const resetExcluded = () => setExcluded(new Set());

  const visibleAccounts = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) => creatorLabel(a).toLowerCase().includes(q) || (a.username ?? '').toLowerCase().includes(q),
    );
  }, [accounts, search]);

  const latestCaptureDay = accounts.reduce<string | null>(
    (acc, a) => (a.latestDay && (!acc || a.latestDay > acc) ? a.latestDay : acc),
    null,
  );

  return (
    <div className="space-y-4 max-w-6xl">
      {/* Title row */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-text">Audience</h1>
          <p className="text-xs text-text-muted">
            Follower and subscriber counts as Maloum reports them, captured once per creator every day
            (03:00 UTC). Totals add up the included creators.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {latestCaptureDay && (
            <span className="text-xs text-text-muted">Last capture: {formatDayLong(latestCaptureDay)}</span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => capture.mutate()}
            loading={capture.isPending || polling}
            title="Take today's snapshot for every creator now"
          >
            {polling ? 'Capturing…' : 'Capture now'}
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <Chip key={r.key} active={range === r.key} onClick={() => setRange(r.key)}>
              {r.label}
            </Chip>
          ))}
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Metric">
          {METRICS.map((m) => (
            <Chip key={m} active={metric === m} onClick={() => setMetric(m)}>
              {METRIC_LABELS[m]}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Creators">
          <Chip
            active={pickerOpen}
            onClick={() => setPickerOpen((v) => !v)}
            title="Choose which creators count towards the total"
          >
            Creators: {excluded.size === 0 ? `all ${accounts.length}` : `${includedCount} of ${accounts.length}`} ▾
          </Chip>
          {excludedList.map((id) => {
            const a = accounts.find((x) => x.accountId === id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggle(id)}
                className="px-2 py-1 text-xs rounded border border-border text-text-muted line-through hover:text-text hover:bg-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                title="Excluded — click to include again"
              >
                {a ? creatorLabel(a) : id.slice(0, 8)} ×
              </button>
            );
          })}
          {excluded.size > 0 && (
            <button
              type="button"
              onClick={resetExcluded}
              className="text-xs text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded px-1"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Creator picker */}
      {pickerOpen && (
        <Card>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Search creators…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
                aria-label="Search creators"
              />
              <Button size="sm" variant="ghost" onClick={resetExcluded} disabled={excluded.size === 0}>
                Include all
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExcluded(new Set(accounts.map((a) => a.accountId)))}
                disabled={accounts.length === 0 || excluded.size === accounts.length}
              >
                Exclude all
              </Button>
              <span className="text-xs text-text-muted ml-auto">
                {includedCount} of {accounts.length} included
              </span>
            </div>
            {accountsQ.isLoading ? (
              <p className="text-xs text-text-muted">Loading creators…</p>
            ) : accountsQ.isError ? (
              <p className="text-xs text-danger">Could not load creators.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
                {visibleAccounts.map((a) => {
                  const included = !excluded.has(a.accountId);
                  return (
                    <label
                      key={a.accountId}
                      className="flex items-center gap-2 text-xs py-1 cursor-pointer hover:bg-bg-hover rounded px-1"
                    >
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => toggle(a.accountId)}
                        className="accent-[var(--color-accent)]"
                      />
                      <span className={included ? 'text-text' : 'text-text-muted line-through'}>
                        {creatorLabel(a)}
                      </span>
                      {a.username && a.displayName && (
                        <span className="text-text-subtle">@{a.username}</span>
                      )}
                      <span className="ml-auto text-text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {a.followers === null ? '—' : formatInt(a.followers)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent>
            <div className="text-xs text-text-muted">{METRIC_LABELS[metric]} now</div>
            <div className="text-3xl font-semibold text-text mt-1">
              {last ? formatInt(last[metric]) : '—'}
            </div>
            <div className="text-xs text-text-subtle mt-1">
              {last ? `as of ${formatDayLong(last.day)}` : 'no snapshot yet'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-xs text-text-muted">Change over {rangeDef.label.toLowerCase()}</div>
            <div
              className={[
                'text-3xl font-semibold mt-1',
                summary && summary.delta > 0 ? 'text-success' : summary && summary.delta < 0 ? 'text-danger' : 'text-text',
              ].join(' ')}
            >
              {summary ? formatSigned(summary.delta) : '—'}
            </div>
            <div className="text-xs text-text-subtle mt-1">
              {summary && points.length > 1
                ? `${summary.pct === null ? '' : `${summary.pct >= 0 ? '+' : ''}${summary.pct.toFixed(1)}% · `}since ${formatDayLong(points[0]!.day)}`
                : 'needs at least two days of snapshots'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-xs text-text-muted">Captured on the latest day</div>
            <div className="text-3xl font-semibold text-text mt-1">
              {last ? `${last.accountsFresh}/${last.accountsTotal}` : '—'}
            </div>
            <div className="text-xs text-text-subtle mt-1">
              {last && last.accountsCovered > last.accountsFresh
                ? `${last.accountsCovered - last.accountsFresh} carried from an earlier day`
                : last
                  ? 'all included creators fresh'
                  : 'creators with a snapshot that day'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="flex items-center justify-between gap-2">
          <CardTitle>
            {METRIC_LABELS[metric]} · {excluded.size === 0 ? 'all creators' : `${includedCount} creators`}
          </CardTitle>
          <div className="flex items-center gap-2">
            {seriesQ.isFetching && !seriesQ.isLoading && (
              <span className="text-xs text-text-subtle">updating…</span>
            )}
            <Button size="sm" variant={showTable ? 'default' : 'ghost'} onClick={() => setShowTable((v) => !v)}>
              {showTable ? 'Chart' : 'Table'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {seriesQ.isLoading ? (
            <p className="text-xs text-text-muted py-8 text-center">Loading…</p>
          ) : seriesQ.isError ? (
            <p className="text-xs text-danger py-8 text-center">Could not load the series.</p>
          ) : points.length === 0 ? (
            <div className="py-10 text-center text-xs text-text-muted space-y-1">
              <p>No snapshots in this window yet.</p>
              <p>
                The first capture runs a few minutes after the API starts and then daily at 03:00 UTC —
                or press <span className="text-text">Capture now</span>.
              </p>
            </div>
          ) : showTable ? (
            <div className="max-h-80 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Day</TableHead>
                    <TableHead className="text-right">{METRIC_LABELS[metric]}</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-right">Captured</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...points].reverse().map((p, i, arr) => {
                    const prev = arr[i + 1];
                    return (
                      <TableRow key={p.day}>
                        <TableCell>{formatDayLong(p.day)}</TableCell>
                        <TableCell className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatInt(p[metric])}
                        </TableCell>
                        <TableCell className="text-right text-text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {prev ? formatSigned(p[metric] - prev[metric]) : '—'}
                        </TableCell>
                        <TableCell className="text-right text-text-muted">
                          {p.accountsFresh}/{p.accountsTotal}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <AudienceChart points={points} metric={metric} fading={seriesQ.isFetching && !seriesQ.isLoading} />
          )}
        </CardContent>
      </Card>

      {/* Per-creator table */}
      <Card>
        <CardHeader>
          <CardTitle>Creators</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {accountsQ.isLoading ? (
            <p className="text-xs text-text-muted">Loading…</p>
          ) : accountsQ.isError ? (
            <p className="text-xs text-danger">Could not load creators.</p>
          ) : accounts.length === 0 ? (
            <p className="text-xs text-text-muted">No live accounts.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Include</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead className="text-right">Followers</TableHead>
                  <TableHead className="text-right">Subscribers</TableHead>
                  <TableHead className="text-right">Followers Δ7d</TableHead>
                  <TableHead>Last snapshot</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((a) => {
                  const included = !excluded.has(a.accountId);
                  const d7 =
                    a.followers !== null && a.followers7dAgo !== null ? a.followers - a.followers7dAgo : null;
                  return (
                    <TableRow key={a.accountId} className={included ? '' : 'opacity-60'}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={included}
                          onChange={() => toggle(a.accountId)}
                          aria-label={`Include ${creatorLabel(a)} in the total`}
                          className="accent-[var(--color-accent)]"
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-text">{creatorLabel(a)}</span>
                        {a.username && a.displayName && (
                          <span className="text-text-subtle ml-1.5 text-xs">@{a.username}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {a.followers === null ? '—' : formatInt(a.followers)}
                      </TableCell>
                      <TableCell className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {a.subscribers === null ? '—' : formatInt(a.subscribers)}
                      </TableCell>
                      <TableCell
                        className={[
                          'text-right',
                          d7 === null ? 'text-text-muted' : d7 > 0 ? 'text-success' : d7 < 0 ? 'text-danger' : 'text-text-muted',
                        ].join(' ')}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {d7 === null ? '—' : formatSigned(d7)}
                      </TableCell>
                      <TableCell className="text-text-muted text-xs" title={a.lastAttemptAt ? `Last attempt ${formatBerlin(a.lastAttemptAt)}` : undefined}>
                        {a.latestDay ? formatDayLong(a.latestDay) : 'never'}
                      </TableCell>
                      <TableCell>{statusBadge(a)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
